// PR TIER1-10 (2026-06-27) — thin wrapper around window.posthog so call sites
// don't have to know about the SDK shape, can't blow up if PostHog ever fails
// to load, and so we can swap providers later by editing one file.
//
// PostHog is loaded synchronously from public/index.html with key
// phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs (api_host us.i.posthog.com).
// On dev/preview the SDK still loads — events flow into PostHog's "Web
// Analytics — Dev" project. To suppress dev noise: posthog.opt_out_capturing()
// at the console.
//
// Adding events: keep names snake_case, past-tense, and pass a flat object of
// properties (no nested objects — PostHog flattens them awkwardly in the UI).
// Document expected properties next to the helper that fires the event below.

const safe = () => (typeof window !== "undefined" && window.posthog) || null;

/**
 * Identify the signed-in user so subsequent events attach to their profile.
 * Call this right after login (and on app boot if a session is still valid).
 *
 * props: { email, role, name } — keep small + non-PII-heavy. Role lets us
 * filter funnels by admin vs broker vs client.
 */
export function identifyUser({ id, email, role, name } = {}) {
  const ph = safe();
  if (!ph || !id) return;
  try {
    ph.identify(id, {
      email: email || undefined,
      role: role || undefined,
      name: name || undefined,
    });
  } catch {
    /* never let analytics break the UI */
  }
}

/**
 * Drop the identified-user state on logout so the next session's events don't
 * stick to the previous user's profile.
 */
export function resetUser() {
  const ph = safe();
  if (!ph) return;
  try { ph.reset(); } catch { /* noop */ }
}

/**
 * Generic capture — every named tracker below funnels through here so we have
 * one place to add structured logging / preview-mode opt-out / etc.
 */
function capture(name, props = {}) {
  const ph = safe();
  if (!ph) return;
  try { ph.capture(name, props); } catch { /* noop */ }
}

// -------- Event vocabulary --------
// Keep this list short and concrete. Adding events is fine; renaming is
// destructive — PostHog dashboards reference the exact name. Adjust dashboards
// if you rename.

/**
 * trackLogin — fired after a successful POST /api/auth/login.
 * props: method ("password" | "impersonation" | "force-password-change")
 */
export function trackLogin(method = "password") {
  capture("login", { method });
}

/**
 * trackDealOpened — fired once per EditableDealPreview mount.
 * props: { dealId, stage, substage, loanAmount }
 * Used to measure "deals touched per broker per day" — the headline activity
 * metric in the Phase 1 observability gap (TIER1-10).
 */
export function trackDealOpened(deal = {}) {
  capture("deal_opened", {
    deal_id: deal.id,
    stage: deal.stage,
    substage: deal.status,
    loan_amount: deal.loan_details?.net_loan_required || deal.loan_amount,
  });
}

/**
 * trackDocPreviewed — fired when DocumentPreviewModal opens (or any
 * openProtectedFile call with mode="preview"). Used to spot brokers who
 * routinely download instead of preview (slow workflow signal).
 * props: { fileName, source } where source is "deal" | "lead" | "envelope" | "email".
 */
export function trackDocPreviewed({ fileName, source } = {}) {
  capture("doc_previewed", {
    file_name: fileName,
    source: source || "unknown",
  });
}

/**
 * trackStatusChanged — fired when a deal substage moves (manual dropdown or
 * auto-tick triggering an advance). Drives the funnel: lead_new → settlement.
 * props: { dealId, fromSubstage, toSubstage, source }
 */
export function trackStatusChanged({ dealId, fromSubstage, toSubstage, source = "manual" } = {}) {
  capture("status_changed", {
    deal_id: dealId,
    from_substage: fromSubstage,
    to_substage: toSubstage,
    source,
  });
}
